import { Component, signal } from '@angular/core';
import { SetupWizardCourseForm } from '../../../../../forms/setup-wizard/setup-wizard-course-form/setup-wizard-course-form';
import { SetupWizardCoursesTable } from './setup-wizard-courses-table/setup-wizard-courses-table';
import { SetupWizard } from '../../../../../../services/setup-wizard';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
	SetupWizardPayloadObjectKeys,
	SetupWizardCourse,
	SetupWizardCourseFormPayload,
	SetupWizardUnitFormPayload,
	SetupWizardSectionFormPayload,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SetupWizardUploadCsvButton } from '../../setup-wizard-upload-csv-button/setup-wizard-upload-csv-button';
import { SetupWizardDownloadCsvButton } from '../../setup-wizard-download-csv-button/setup-wizard-download-csv-button';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { SetupWizardUnitForm } from '../../../../../forms/setup-wizard/setup-wizard-unit-form/setup-wizard-unit-form';
import { SetupWizardSectionForm } from '../../../../../forms/setup-wizard/setup-wizard-section-form/setup-wizard-section-form';

@Component({
	selector: 'app-setup-wizard-course-editor',
	imports: [
		SetupWizardCourseForm,
		SetupWizardCoursesTable,
		NzButtonModule,
		SetupWizardUploadCsvButton,
		SetupWizardDownloadCsvButton,
		NzTabsModule,
		NzButtonModule,
		SetupWizardUnitForm,
		SetupWizardSectionForm,
	],
	standalone: true,
	templateUrl: './setup-wizard-course-editor.html',
	styleUrl: './setup-wizard-course-editor.scss',
})
export class SetupWizardCourseEditor {
	public edit = signal<SetupWizardCourse | null>(null);
	public showForm = signal<boolean>(false);
	public loading = signal<boolean>(false);
	private clientUuid: string | undefined;
	private sub?: Subscription;
	public SetupWizardPayloadObjectKeys = SetupWizardPayloadObjectKeys;

	constructor(
		private route: ActivatedRoute,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	public onFormSubmit(payload: SetupWizardCourseFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.courses, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onUnitFormSubmit(payload: SetupWizardUnitFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.units, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onSectionFormSubmit(payload: SetupWizardSectionFormPayload) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		this.loading.set(true);

		try {
			this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.sections, payload);
		} catch (err: unknown) {
			console.error('Error submitting form in editor:', err);
		} finally {
			this.loading.set(false);
			this.showForm.set(false);
			this.edit.set(null);
		}
	}

	public onFormClear() {
		this.showForm.set(false);
		this.edit.set(null);
	}

	public onAdd() {
		this.showForm.set(true);
		this.edit.set(null);
	}

	public onEdit($event: SetupWizardCourse) {
		if ($event.after || $event.before) {
			this.edit.set($event);
			this.showForm.set(true);
		}
	}

	public async onToggleDisable($event: SetupWizardCourse) {
		const beforeAfter = $event.after || $event.before;

		if (!beforeAfter) {
			throw new Error('No course data available to update');
		}

		await this.setupWizard.upsertPayloadItem(SetupWizardPayloadObjectKeys.courses, {
			...beforeAfter,
			active: !beforeAfter.active,
		});
	}

	public async onReset($event: SetupWizardCourse) {
		if (!$event.after) {
			throw new Error('No course data available to reset');
		}

		await this.setupWizard.resetPayloadItem(SetupWizardPayloadObjectKeys.courses, $event.after);
	}

	public async onRemove($event: SetupWizardCourse) {
		if (!$event.after) {
			throw new Error('No course data available to remove');
		}

		await this.setupWizard.removePayloadItem(SetupWizardPayloadObjectKeys.courses, $event.after);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
